import * as i0 from '@angular/core';
import { forwardRef, ElementRef, Input, Inject, ViewEncapsulation, Component, EventEmitter, Renderer2, HostListener, ViewChildren, ViewChild, Output, Injectable, Injector, Directive, HostBinding, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import * as i2 from '@angular/forms';
import { NG_VALUE_ACCESSOR, FormsModule, DefaultValueAccessor, NgControl, Validators, ReactiveFormsModule } from '@angular/forms';
import * as i1$1 from '@angular/cdk/overlay';
import { OverlayConfig, Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Subject, Subscription, BehaviorSubject, noop } from 'rxjs';
import * as i1 from '@angular/cdk/a11y';
import { CdkTrapFocus, FocusKeyManager, A11yModule } from '@angular/cdk/a11y';
import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import * as i2$1 from '@angular/common';
import { NgClass, CommonModule } from '@angular/common';
import { debounceTime, distinctUntilChanged, filter, tap } from 'rxjs/operators';
import { CentricIcon2Component, CentricIcon2Module } from '@windmill/ng-windmill/icon';
import { WindmillLoaderComponent, WindmillLoaderModule } from '@windmill/ng-windmill/loader';
import { MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';

const DEFAULT_MIN_BUFFER_PX = 100;
const DEFAULT_MAX_BUFFER_PX = 200;

class WindmillItemListComponent {
    elementRef;
    /**
     * Text for item
     */
    name;
    /**
     * Property is used to enable multiple selection for the component
     */
    multiple = false;
    /**
     * Whether the component is selected
     */
    checked = false;
    /**
     * Maximum number of lines for items text
     */
    maxLinesNumber = '1';
    htmlElement;
    constructor(elementRef) {
        this.elementRef = elementRef;
        this.htmlElement = this.elementRef.nativeElement;
    }
    focus() {
        this.elementRef.nativeElement.focus();
    }
    setTabIndex(index) {
        if (index === null) {
            this.htmlElement.removeAttribute('tabindex');
        }
        else {
            this.htmlElement.setAttribute('tabindex', index);
        }
    }
    getTabIndex() {
        return this.htmlElement.getAttribute('tabindex');
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillItemListComponent, deps: [{ token: forwardRef(() => ElementRef) }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: WindmillItemListComponent, isStandalone: true, selector: "windmill-item-list", inputs: { name: "name", multiple: "multiple", checked: "checked", maxLinesNumber: "maxLinesNumber" }, ngImport: i0, template: "<div class=\"item-list-container\">\r\n  @if (multiple) {\r\n    <div\r\n      class=\"windmill-pseudo-checkbox\"\r\n      [class.is-checked]=\"checked\">\r\n      <span class=\"checkbox-inner-container\">\r\n        <span class=\"checkbox-frame\"></span>\r\n        <span class=\"checkbox-background\"></span>\r\n      </span>\r\n    </div>\r\n  }\r\n  <span class=\"list-item-name line-clamp-{{ maxLinesNumber }}\">\r\n    {{ name }}\r\n  </span>\r\n</div>\r\n", encapsulation: i0.ViewEncapsulation.None });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillItemListComponent, decorators: [{
            type: Component,
            args: [{ selector: 'windmill-item-list', encapsulation: ViewEncapsulation.None, standalone: true, template: "<div class=\"item-list-container\">\r\n  @if (multiple) {\r\n    <div\r\n      class=\"windmill-pseudo-checkbox\"\r\n      [class.is-checked]=\"checked\">\r\n      <span class=\"checkbox-inner-container\">\r\n        <span class=\"checkbox-frame\"></span>\r\n        <span class=\"checkbox-background\"></span>\r\n      </span>\r\n    </div>\r\n  }\r\n  <span class=\"list-item-name line-clamp-{{ maxLinesNumber }}\">\r\n    {{ name }}\r\n  </span>\r\n</div>\r\n" }]
        }], ctorParameters: () => [{ type: i0.ElementRef, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => ElementRef)]
                }] }], propDecorators: { name: [{
                type: Input
            }], multiple: [{
                type: Input
            }], checked: [{
                type: Input
            }], maxLinesNumber: [{
                type: Input
            }] } });

const WINDMILL_DROPDOWN_LIST_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => WindmillDropdownSearchListComponent),
    multi: true
};
const MAXIMUM_NO_OF_ITEMS = 9;
const ITEM_SIZE = 48;
const FILTER_AND_MARGIN_VALUE = 56;
const MAXIMUM_VH_VALUE = 100;
const DEBOUNCE_TIME = 200;
const cdkComponents = [CdkTrapFocus, CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport];
const windmillComponents = [CentricIcon2Component, WindmillLoaderComponent];
/**
 * To use this component in a module, you can import the `WindmillDropdownSearchModule`:
 * @example
 * ```typescript
 * import { WindmillDropdownSearchModule } from '@windmill/ng-windmill/dropdown-search';
 * ```
 *
 * To use the standalone component directly, you can import `WindmillDropdownSearchListComponent`:
 * @example
 * ```typescript
 * import { WindmillDropdownSearchListComponent } from '@windmill/ng-windmill/dropdown-search';
 * ```
 */
class WindmillDropdownSearchListComponent {
    renderer2;
    elementRef;
    liveAnnouncer;
    emptyMessage = 'No items available';
    listPlaceholder;
    positions;
    isOpened = false;
    highlightedItem;
    testInputSearchId;
    listId;
    testListId;
    displayPropertyName;
    valuePropertyName;
    multiple = false;
    maxLinesNumber = '1';
    maxHeight;
    hasLoader = true;
    minBufferPx = DEFAULT_MIN_BUFFER_PX;
    maxBufferPx = DEFAULT_MAX_BUFFER_PX;
    loadingText;
    ariaLabelInput;
    ariaDescriptionInput;
    ariaLabelClearIcon;
    ariaLabelList;
    ariaDescriptionList;
    ariaLabelledbyList;
    ariaDescribedbyList;
    filteredSourceProject$;
    initialItemsSource$;
    preselectedItems$;
    multipleDropdownSearch$;
    selectedItem$;
    searchValueChange$;
    listSearchValueChange$ = new Subject();
    listItemsChange$;
    items;
    hasLoader$;
    innerBlur = new EventEmitter();
    isClosed = new EventEmitter();
    viewPort;
    listElement;
    filterInputElement;
    itemsList;
    searchValue;
    value;
    itemSize = ITEM_SIZE;
    autocomplete = 'off';
    hasClearIcon = true;
    filteredSource;
    get initialSource() {
        return this._initialSource;
    }
    set initialSource(value) {
        const finishedLoading = this._initialSource?.length === 0 && value.length > 0;
        this._initialSource = value;
        if (finishedLoading) {
            setTimeout(() => {
                this._setUpKeyboardNavigation();
            });
        }
    }
    _initialSource;
    keyManager;
    initialItemsSourceSubscription = Subscription.EMPTY;
    filteredSourceProjectSubscription = Subscription.EMPTY;
    preselectedItemsSubscription = Subscription.EMPTY;
    searchValueChangesSubscription = Subscription.EMPTY;
    scrollIndexChangeSubscription = Subscription.EMPTY;
    loaderChangeSubscription = Subscription.EMPTY;
    get _activeItemIndex() {
        const selectedItem = this.filteredSource?.find(element => this.isItemSelected(element.itemWithChecked));
        return selectedItem ? this.filteredSource?.indexOf(selectedItem) : 0;
    }
    constructor(renderer2, elementRef, liveAnnouncer) {
        this.renderer2 = renderer2;
        this.elementRef = elementRef;
        this.liveAnnouncer = liveAnnouncer;
        this.initializeAllBehaviorSubjects();
    }
    ngOnInit() {
        this.subscribeToSearchValueChanges();
        this.subscribeToPreselectedItems();
        this.subscribeToInitialSourceChange();
        this.subscribeToFilteredSourceChange();
        this.subscribeToLoaderChanges();
        this.setDropdownListHeight(true);
    }
    ngAfterViewInit() {
        this.positionSearchList();
        this._setUpKeyboardNavigation();
    }
    ngOnDestroy() {
        this.unsubscribeAll();
    }
    onKeyboardChange(event) {
        this.handleEscapeKey(event);
    }
    onKeydownList(event) {
        this.preventKeyboard(event);
        if (event.key === 'Tab' && !this.showsOptionsOrEmptyMessage) {
            this.isClosed.emit();
            return;
        }
        this.keyManager.onKeydown(event);
    }
    onKeyUp(event, i) {
        if (['Enter', ' ', 'Spacebar'].includes(event.key)) {
            this.selectItem(i);
        }
    }
    onFocusOutFromFilterInput(event) {
        const relatedTarget = event.relatedTarget;
        if (relatedTarget && relatedTarget instanceof HTMLElement && relatedTarget.classList.contains('cdk-focus-trap-anchor')) {
            this.viewPort?.scrollToIndex(this._activeItemIndex);
            setTimeout(() => {
                this.keyManager.setActiveItem(this._activeItemIndex);
            });
        }
    }
    onModelChanged(event) {
        this.searchValue = event;
        this.listSearchValueChange$.next(event);
    }
    onClearValue() {
        this.searchValue = null;
        this.listSearchValueChange$.next(null);
        this.keyManager.setFirstItemActive();
        this.filterInputElement.nativeElement.focus();
    }
    selectItem(indexItem) {
        if (this.multiple) {
            this.filteredSource[indexItem].checked = !this.filteredSource[indexItem].checked;
            const selectedItem = this.filteredSource[indexItem];
            const initialElement = this.initialSource?.find(item => item.itemWithChecked === selectedItem.itemWithChecked);
            if (initialElement) {
                initialElement.checked = selectedItem.checked;
            }
            this.multipleDropdownSearch$.next(this.initialSource);
        }
        this.value = this.getValueFor(this.items[indexItem]);
        this.selectedItem$.next(this.value);
    }
    getValueFor(item) {
        return item && this.valuePropertyName ? this.getComplexValueProperty(item) : item;
    }
    getNameFor(item) {
        return item && this.displayPropertyName ? this.getComplexDisplayedValue(item) : item;
    }
    isItemSelected(item) {
        if (this.multiple) {
            const itemSource = this.initialSource?.find(element => element.itemWithChecked === item);
            return Boolean(itemSource?.checked);
        }
        else if (typeof this.highlightedItem === 'object' && !this.valuePropertyName) {
            return Boolean(this.highlightedItem &&
                (this.getNameFor(this.highlightedItem) === this.getNameFor(item) ||
                    this.getValueFor(this.highlightedItem) === this.getValueFor(item)));
        }
        return Boolean((this.highlightedItem || this.highlightedItem === 0) &&
            (this.highlightedItem === this.getNameFor(item) || this.highlightedItem === this.getValueFor(item)));
    }
    getAriaChecked(item) {
        return this.multiple ? this.isItemSelected(item) : null;
    }
    getAriaSelected(item) {
        return this.multiple ? null : this.isItemSelected(item);
    }
    get showsOptionsOrEmptyMessage() {
        return Boolean(this.initialSource?.length) || !this.hasLoader;
    }
    get hasResults() {
        return this.items ? this.items?.length > 0 : false;
    }
    get focusedIndex() {
        return this.keyManager.activeItemIndex;
    }
    get maxHeightClass() {
        return this.showsOptionsOrEmptyMessage ? `height-${this.maxHeight}` : null;
    }
    setListWidthInHtml() {
        const listWrapperElement = this.elementRef.nativeElement.querySelector('.windmill-dropdown-search-list-content');
        if (listWrapperElement) {
            this.renderer2.setStyle(listWrapperElement, 'width', `${this.positions.width?.toString()}px`);
        }
    }
    _setUpKeyboardNavigation() {
        setTimeout(() => {
            this.viewPort?.scrollToIndex(this._activeItemIndex);
            this.initializeTabIndexForListItems();
        });
        this.focusInput();
        this.scrollIndexChangeSubscription.unsubscribe();
        this.scrollIndexChangeSubscription = this.viewPort.scrolledIndexChange.subscribe(() => {
            this.initializeTabIndexForListItems();
        });
        this._initializeKeyManager();
    }
    initializeTabIndexForListItems() {
        this.itemsList?.forEach((listItem) => {
            listItem.setTabIndex('-1');
        });
    }
    _initializeKeyManager() {
        if (!this.keyManager) {
            this.keyManager = new FocusKeyManager(this.itemsList);
        }
    }
    initializeAllBehaviorSubjects() {
        this.searchValueChange$ = new BehaviorSubject(this.searchValue);
        this.selectedItem$ = new BehaviorSubject(this.value);
        this.multipleDropdownSearch$ = new BehaviorSubject(this.value);
        this.initialItemsSource$ = new BehaviorSubject(null);
        this.filteredSourceProject$ = new BehaviorSubject(null);
        this.preselectedItems$ = new BehaviorSubject(null);
        this.listItemsChange$ = new BehaviorSubject(null);
        this.hasLoader$ = new BehaviorSubject(this.hasLoader);
    }
    subscribeToLoaderChanges() {
        this.loaderChangeSubscription = this.hasLoader$.subscribe(hasLoader => {
            this.hasLoader = hasLoader;
            if (!this.hasLoader && this.initialSource?.length === 0) {
                setTimeout(() => {
                    this.filterInputElement?.nativeElement.focus();
                });
            }
        });
    }
    subscribeToPreselectedItems() {
        this.preselectedItemsSubscription = this.preselectedItems$.subscribe(value => {
            this.highlightedItem = value;
        });
    }
    subscribeToInitialSourceChange() {
        this.initialItemsSourceSubscription = this.initialItemsSource$.subscribe(source => {
            if (this.initialSource && this.initialSource.length === 0) {
                this.listItemsChange$.next(null);
            }
            const sourceWithChecked = this.createSourceWithChecked(source);
            this.initialSource = Object.assign(sourceWithChecked);
            this.setDropdownListHeight(true);
        });
    }
    subscribeToFilteredSourceChange() {
        this.filteredSourceProjectSubscription = this.filteredSourceProject$.subscribe(source => {
            if (!source) {
                return;
            }
            this.items = source;
            const sourceWithChecked = this.createSourceWithChecked(this.items);
            this.filteredSource = Object.assign(sourceWithChecked);
            this.announceEmptyState();
            this.setDropdownListHeight(true);
        });
    }
    subscribeToSearchValueChanges() {
        this.searchValueChangesSubscription = this.listSearchValueChange$
            .pipe(debounceTime(DEBOUNCE_TIME), distinctUntilChanged(), filter(value => typeof value !== 'undefined'), tap(() => this.scrollToTop()))
            .subscribe((value) => {
            this.searchValueChange$.next(value);
            setTimeout(() => {
                this.listItemsChange$.next(null);
                this.setDropdownListHeight(false, value);
            });
        });
    }
    unsubscribeAll() {
        this.searchValueChangesSubscription.unsubscribe();
        this.initialItemsSourceSubscription.unsubscribe();
        this.filteredSourceProjectSubscription.unsubscribe();
        this.preselectedItemsSubscription.unsubscribe();
        this.scrollIndexChangeSubscription.unsubscribe();
        this.loaderChangeSubscription.unsubscribe();
    }
    setDropdownListHeight(isInitial, filterValue) {
        const dropdownSearchListNativeElement = this.elementRef.nativeElement;
        const dropdownVirtualScrollViewport = dropdownSearchListNativeElement?.querySelector('.cdk-virtual-scroll-viewport');
        if (typeof this.items === 'undefined' || this.items?.length === 0) {
            this.handleEmptyMessageOrLoaderHeight(dropdownSearchListNativeElement, dropdownVirtualScrollViewport);
            return;
        }
        if (isInitial || filterValue === '') {
            this.handleRegularListHeight(dropdownVirtualScrollViewport);
            return;
        }
        this.handleFilteredListHeight(dropdownVirtualScrollViewport);
    }
    handleEmptyMessageOrLoaderHeight(listNativeElement, viewport) {
        const emptyMessageHeight = this.hasLoader ? ITEM_SIZE : listNativeElement?.querySelector('.empty-message')?.clientHeight;
        this.renderer2.setStyle(viewport, 'height', `${emptyMessageHeight}px`);
    }
    handleFilteredListHeight(viewport) {
        let measuredListHeight = 0;
        setTimeout(() => {
            if (this.viewPort) {
                measuredListHeight = this.viewPort?.measureRenderedContentSize();
                if (measuredListHeight < MAXIMUM_NO_OF_ITEMS * ITEM_SIZE) {
                    this.renderer2.setStyle(viewport, 'height', this._convertPxToVh(measuredListHeight));
                }
                else {
                    this.handleRegularListHeight(viewport);
                }
            }
        });
    }
    handleRegularListHeight(viewport) {
        if (this.items?.length < MAXIMUM_NO_OF_ITEMS) {
            this.renderer2.setStyle(viewport, 'height', this._convertPxToVh(this.items.length * ITEM_SIZE));
        }
        else {
            this.renderer2.setStyle(viewport, 'height', this._convertPxToVh(MAXIMUM_NO_OF_ITEMS * ITEM_SIZE));
        }
    }
    _convertPxToVh(listPxValue) {
        const listVhValue = (MAXIMUM_VH_VALUE * listPxValue) / window.innerHeight;
        const filterAndMarginVhValue = (MAXIMUM_VH_VALUE * FILTER_AND_MARGIN_VALUE) / window.innerHeight;
        const listAndFiltersVhValue = listVhValue + filterAndMarginVhValue;
        const maxListVhValue = MAXIMUM_VH_VALUE - filterAndMarginVhValue;
        return listAndFiltersVhValue > MAXIMUM_VH_VALUE ? `${maxListVhValue}vh` : `${listVhValue}vh`;
    }
    announceEmptyState() {
        if (this.items.length === 0 && this.emptyMessage && !this.hasLoader) {
            this.liveAnnouncer.announce(`${this.emptyMessage}`).catch(() => noop);
        }
    }
    focusInput() {
        const dropdownSearchListNativeElement = this.elementRef.nativeElement;
        const input = dropdownSearchListNativeElement.querySelector('.input-element');
        setTimeout(() => input?.focus());
    }
    createSourceWithChecked(array) {
        const itemsSourceWithChecked = [];
        const preselectedItems = Array.isArray(this.highlightedItem) ? this.highlightedItem : [this.highlightedItem];
        array?.forEach(item => {
            let checked = false;
            if (this.displayPropertyName) {
                checked = preselectedItems.includes(this.getValueFor(item));
            }
            else {
                checked = preselectedItems.includes(item);
            }
            const itemWithChecked = {
                itemWithChecked: item,
                checked
            };
            itemsSourceWithChecked.push(itemWithChecked);
        });
        return itemsSourceWithChecked;
    }
    getComplexValueProperty(item) {
        return this.valuePropertyName
            ? item[this.valuePropertyName]
            : item;
    }
    getComplexDisplayedValue(item) {
        return String(item[this.displayPropertyName]);
    }
    scrollToTop() {
        setTimeout(() => {
            if (this.viewPort) {
                this.viewPort.scrollToIndex(0);
            }
        });
    }
    positionSearchList() {
        const dropdownSearchListNativeElement = this.elementRef.nativeElement;
        const dropdownSearchList = dropdownSearchListNativeElement.querySelectorAll('.windmill-dropdown-search-list-content');
        this.renderer2.setStyle(dropdownSearchList[0], 'width', `${this.positions.width}px`);
        this.renderer2.setStyle(dropdownSearchList[0], 'top', `${this.positions.bottom}px`);
        this.renderer2.setStyle(dropdownSearchList[0], 'left', `${this.positions.left}px`);
    }
    handleEscapeKey(event) {
        if (event.key === 'Escape' || event.key === 'Esc') {
            event.stopPropagation();
            this.isClosed.emit();
        }
    }
    preventKeyboard(event) {
        if (((event.key === ' ' || event.key === 'Spacebar') && event.target.nodeName === 'WINDMILL-ITEM-LIST') ||
            ['End', 'Home'].includes(event.key)) {
            event.preventDefault();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillDropdownSearchListComponent, deps: [{ token: forwardRef(() => Renderer2) }, { token: forwardRef(() => ElementRef) }, { token: i1.LiveAnnouncer }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: WindmillDropdownSearchListComponent, isStandalone: true, selector: "windmill-dropdown-search-list", outputs: { innerBlur: "innerBlur", isClosed: "isClosed" }, host: { listeners: { "keydown": "onKeyboardChange($event)" } }, providers: [WINDMILL_DROPDOWN_LIST_VALUE_ACCESSOR], viewQueries: [{ propertyName: "viewPort", first: true, predicate: CdkVirtualScrollViewport, descendants: true }, { propertyName: "listElement", first: true, predicate: ["listElement"], descendants: true }, { propertyName: "filterInputElement", first: true, predicate: ["filterInputElement"], descendants: true }, { propertyName: "itemsList", predicate: i0.forwardRef(() => WindmillItemListComponent), descendants: true }], ngImport: i0, template: "<section\r\n  class=\"windmill-dropdown-search-list-content\"\r\n  cdkTrapFocus>\r\n  @if (showsOptionsOrEmptyMessage) {\r\n    <div class=\"windmill-dropdown-search-list-content-input-icon\">\r\n      <div\r\n        class=\"windmill-input small\"\r\n        [class.has-value]=\"searchValue\"\r\n        [class.has-clear-icon]=\"searchValue\"\r\n        (focusout)=\"onFocusOutFromFilterInput($event)\">\r\n        <input\r\n          #filterInputElement\r\n          type=\"text\"\r\n          class=\"input-element\"\r\n          [attr.autocomplete]=\"autocomplete\"\r\n          [attr.data-testid]=\"testInputSearchId\"\r\n          [attr.placeholder]=\"listPlaceholder\"\r\n          [attr.aria-label]=\"ariaLabelInput\"\r\n          [attr.aria-description]=\"ariaDescriptionInput\"\r\n          [ngModel]=\"searchValue\"\r\n          (ngModelChange)=\"onModelChanged($event)\" />\r\n        @if (searchValue) {\r\n          <button\r\n            class=\"clear-button\"\r\n            type=\"button\"\r\n            [attr.aria-label]=\"ariaLabelClearIcon\"\r\n            (click)=\"onClearValue()\">\r\n            <centric-icon2\r\n              class=\"clear-icon\"\r\n              [svgIcon]=\"'cancel_r'\"\r\n              [size]=\"'16'\">\r\n            </centric-icon2>\r\n          </button>\r\n        }\r\n        <centric-icon2\r\n          class=\"search-icon\"\r\n          [svgIcon]=\"'search_r'\"\r\n          [size]=\"'24'\">\r\n        </centric-icon2>\r\n      </div>\r\n    </div>\r\n  }\r\n  <cdk-virtual-scroll-viewport\r\n    appendOnly\r\n    [ngClass]=\"maxHeightClass\"\r\n    [attr.role]=\"showsOptionsOrEmptyMessage ? 'listbox' : null\"\r\n    [attr.tabindex]=\"-1\"\r\n    [attr.id]=\"showsOptionsOrEmptyMessage ? listId : null\"\r\n    [attr.data-testid]=\"showsOptionsOrEmptyMessage ? testListId : null\"\r\n    [attr.aria-label]=\"showsOptionsOrEmptyMessage ? ariaLabelList : null\"\r\n    [attr.aria-description]=\"showsOptionsOrEmptyMessage ? ariaDescriptionList : null\"\r\n    [attr.aria-labelledby]=\"showsOptionsOrEmptyMessage ? ariaLabelledbyList : null\"\r\n    [attr.aria-describedby]=\"showsOptionsOrEmptyMessage ? ariaDescribedbyList : null\"\r\n    [itemSize]=\"itemSize\"\r\n    [minBufferPx]=\"minBufferPx\"\r\n    [maxBufferPx]=\"maxBufferPx\"\r\n    (keydown)=\"onKeydownList($event)\">\r\n    @if (showsOptionsOrEmptyMessage) {\r\n      <ul\r\n        #listElement\r\n        class=\"dropdown-content-list\"\r\n        role=\"none\"\r\n        [class.multiple]=\"multiple\"\r\n        [class.has-results]=\"hasResults\">\r\n        <li\r\n          *cdkVirtualFor=\"let item of filteredSource; let i = index\"\r\n          role=\"none\"\r\n          [class.is-selected]=\"isItemSelected(item.itemWithChecked)\"\r\n          (keyup)=\"onKeyUp($event, i)\"\r\n          (click)=\"selectItem(i)\">\r\n          <windmill-item-list\r\n            role=\"option\"\r\n            [name]=\"getNameFor(item.itemWithChecked)\"\r\n            [multiple]=\"multiple\"\r\n            [checked]=\"item.checked\"\r\n            [maxLinesNumber]=\"maxLinesNumber\"\r\n            [attr.aria-checked]=\"getAriaChecked(item.itemWithChecked)\"\r\n            [attr.aria-selected]=\"getAriaSelected(item.itemWithChecked)\">\r\n          </windmill-item-list>\r\n        </li>\r\n        @if (items?.length === 0 || !items) {\r\n          <li class=\"empty-message\">\r\n            {{ emptyMessage }}\r\n          </li>\r\n        }\r\n      </ul>\r\n    } @else {\r\n      <div class=\"loader-wrapper\">\r\n        <windmill-loader\r\n          [size]=\"24\"\r\n          [loadingText]=\"loadingText\"></windmill-loader>\r\n      </div>\r\n    }\r\n  </cdk-virtual-scroll-viewport>\r\n</section>\r\n", dependencies: [{ kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "component", type: WindmillItemListComponent, selector: "windmill-item-list", inputs: ["name", "multiple", "checked", "maxLinesNumber"] }, { kind: "directive", type: CdkTrapFocus, selector: "[cdkTrapFocus]", inputs: ["cdkTrapFocus", "cdkTrapFocusAutoCapture"], exportAs: ["cdkTrapFocus"] }, { kind: "directive", type: CdkFixedSizeVirtualScroll, selector: "cdk-virtual-scroll-viewport[itemSize]", inputs: ["itemSize", "minBufferPx", "maxBufferPx"] }, { kind: "directive", type: CdkVirtualForOf, selector: "[cdkVirtualFor][cdkVirtualForOf]", inputs: ["cdkVirtualForOf", "cdkVirtualForTrackBy", "cdkVirtualForTemplate", "cdkVirtualForTemplateCacheSize"] }, { kind: "component", type: CdkVirtualScrollViewport, selector: "cdk-virtual-scroll-viewport", inputs: ["orientation", "appendOnly"], outputs: ["scrolledIndexChange"] }, { kind: "component", type: CentricIcon2Component, selector: "centric-icon2", inputs: ["size", "inline", "svgIcon"] }, { kind: "component", type: WindmillLoaderComponent, selector: "windmill-loader", inputs: ["loadingText", "ariaLabel", "ariaDescription", "ariaLabelledby", "ariaDescribedby", "size"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillDropdownSearchListComponent, decorators: [{
            type: Component,
            args: [{ selector: 'windmill-dropdown-search-list', imports: [FormsModule, NgClass, WindmillItemListComponent, ...cdkComponents, ...windmillComponents], providers: [WINDMILL_DROPDOWN_LIST_VALUE_ACCESSOR], standalone: true, template: "<section\r\n  class=\"windmill-dropdown-search-list-content\"\r\n  cdkTrapFocus>\r\n  @if (showsOptionsOrEmptyMessage) {\r\n    <div class=\"windmill-dropdown-search-list-content-input-icon\">\r\n      <div\r\n        class=\"windmill-input small\"\r\n        [class.has-value]=\"searchValue\"\r\n        [class.has-clear-icon]=\"searchValue\"\r\n        (focusout)=\"onFocusOutFromFilterInput($event)\">\r\n        <input\r\n          #filterInputElement\r\n          type=\"text\"\r\n          class=\"input-element\"\r\n          [attr.autocomplete]=\"autocomplete\"\r\n          [attr.data-testid]=\"testInputSearchId\"\r\n          [attr.placeholder]=\"listPlaceholder\"\r\n          [attr.aria-label]=\"ariaLabelInput\"\r\n          [attr.aria-description]=\"ariaDescriptionInput\"\r\n          [ngModel]=\"searchValue\"\r\n          (ngModelChange)=\"onModelChanged($event)\" />\r\n        @if (searchValue) {\r\n          <button\r\n            class=\"clear-button\"\r\n            type=\"button\"\r\n            [attr.aria-label]=\"ariaLabelClearIcon\"\r\n            (click)=\"onClearValue()\">\r\n            <centric-icon2\r\n              class=\"clear-icon\"\r\n              [svgIcon]=\"'cancel_r'\"\r\n              [size]=\"'16'\">\r\n            </centric-icon2>\r\n          </button>\r\n        }\r\n        <centric-icon2\r\n          class=\"search-icon\"\r\n          [svgIcon]=\"'search_r'\"\r\n          [size]=\"'24'\">\r\n        </centric-icon2>\r\n      </div>\r\n    </div>\r\n  }\r\n  <cdk-virtual-scroll-viewport\r\n    appendOnly\r\n    [ngClass]=\"maxHeightClass\"\r\n    [attr.role]=\"showsOptionsOrEmptyMessage ? 'listbox' : null\"\r\n    [attr.tabindex]=\"-1\"\r\n    [attr.id]=\"showsOptionsOrEmptyMessage ? listId : null\"\r\n    [attr.data-testid]=\"showsOptionsOrEmptyMessage ? testListId : null\"\r\n    [attr.aria-label]=\"showsOptionsOrEmptyMessage ? ariaLabelList : null\"\r\n    [attr.aria-description]=\"showsOptionsOrEmptyMessage ? ariaDescriptionList : null\"\r\n    [attr.aria-labelledby]=\"showsOptionsOrEmptyMessage ? ariaLabelledbyList : null\"\r\n    [attr.aria-describedby]=\"showsOptionsOrEmptyMessage ? ariaDescribedbyList : null\"\r\n    [itemSize]=\"itemSize\"\r\n    [minBufferPx]=\"minBufferPx\"\r\n    [maxBufferPx]=\"maxBufferPx\"\r\n    (keydown)=\"onKeydownList($event)\">\r\n    @if (showsOptionsOrEmptyMessage) {\r\n      <ul\r\n        #listElement\r\n        class=\"dropdown-content-list\"\r\n        role=\"none\"\r\n        [class.multiple]=\"multiple\"\r\n        [class.has-results]=\"hasResults\">\r\n        <li\r\n          *cdkVirtualFor=\"let item of filteredSource; let i = index\"\r\n          role=\"none\"\r\n          [class.is-selected]=\"isItemSelected(item.itemWithChecked)\"\r\n          (keyup)=\"onKeyUp($event, i)\"\r\n          (click)=\"selectItem(i)\">\r\n          <windmill-item-list\r\n            role=\"option\"\r\n            [name]=\"getNameFor(item.itemWithChecked)\"\r\n            [multiple]=\"multiple\"\r\n            [checked]=\"item.checked\"\r\n            [maxLinesNumber]=\"maxLinesNumber\"\r\n            [attr.aria-checked]=\"getAriaChecked(item.itemWithChecked)\"\r\n            [attr.aria-selected]=\"getAriaSelected(item.itemWithChecked)\">\r\n          </windmill-item-list>\r\n        </li>\r\n        @if (items?.length === 0 || !items) {\r\n          <li class=\"empty-message\">\r\n            {{ emptyMessage }}\r\n          </li>\r\n        }\r\n      </ul>\r\n    } @else {\r\n      <div class=\"loader-wrapper\">\r\n        <windmill-loader\r\n          [size]=\"24\"\r\n          [loadingText]=\"loadingText\"></windmill-loader>\r\n      </div>\r\n    }\r\n  </cdk-virtual-scroll-viewport>\r\n</section>\r\n" }]
        }], ctorParameters: () => [{ type: i0.Renderer2, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => Renderer2)]
                }] }, { type: i0.ElementRef, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => ElementRef)]
                }] }, { type: i1.LiveAnnouncer }], propDecorators: { innerBlur: [{
                type: Output
            }], isClosed: [{
                type: Output
            }], viewPort: [{
                type: ViewChild,
                args: [CdkVirtualScrollViewport]
            }], listElement: [{
                type: ViewChild,
                args: ['listElement']
            }], filterInputElement: [{
                type: ViewChild,
                args: ['filterInputElement']
            }], itemsList: [{
                type: ViewChildren,
                args: [forwardRef(() => WindmillItemListComponent)]
            }], onKeyboardChange: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

/**
 * import { WindmillDropdownSearchModule } from '@windmill/ng-windmill/dropdown-search';
 */
class WindmillDropdownSearchOverlayService {
    overlay;
    hasLoader = true;
    unfilteredSource;
    overlayRef;
    closed = new EventEmitter();
    dropdownWidthChange = new BehaviorSubject(0);
    componentRef;
    positions = [
        {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
            offsetY: 4
        },
        {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom',
            offsetY: -4
        },
        {
            originX: 'center',
            originY: 'center',
            overlayX: 'center',
            overlayY: 'center'
        }
    ];
    _backdropClickEventSubscription = Subscription.EMPTY;
    _keydownEventSubscription = Subscription.EMPTY;
    _listItemsChangeEventSubscription = Subscription.EMPTY;
    _dropdownWidthChangeSubscription = Subscription.EMPTY;
    constructor(overlay) {
        this.overlay = overlay;
    }
    ngOnDestroy() {
        this._backdropClickEventSubscription.unsubscribe();
        this._keydownEventSubscription.unsubscribe();
        this._listItemsChangeEventSubscription.unsubscribe();
    }
    setSource(unfilteredSource) {
        this.unfilteredSource = unfilteredSource;
    }
    openDropdownList(listProperties) {
        const overlayClass = listProperties.overlayClass ?? '';
        this.overlayRef = this.createOverlay(listProperties.elementRef, overlayClass);
        const componentRef = this.attachDropdownList(this.overlayRef);
        this.componentRef = componentRef;
        const dropdownSearchListComponent = componentRef.instance;
        this._listItemsChangeEventSubscription = dropdownSearchListComponent?.listItemsChange$.subscribe(() => {
            this.overlayRef.updatePosition();
        });
        this._updateProperties(dropdownSearchListComponent, listProperties);
        this._subscribeToDropdownWidthChange();
        return componentRef;
    }
    updateLayoutListProperties(dropdownSearchListComponent, listProperties) {
        dropdownSearchListComponent.positions = listProperties.positions;
        dropdownSearchListComponent.listPlaceholder = listProperties.listPlaceholder;
        dropdownSearchListComponent.testListId = listProperties.testListId;
        dropdownSearchListComponent.testInputSearchId = listProperties.testInputSearchId;
        dropdownSearchListComponent.maxLinesNumber = listProperties.maxLinesNumber;
        dropdownSearchListComponent.maxHeight = listProperties.maxHeight;
        dropdownSearchListComponent.hasLoader = listProperties.hasLoader;
        this.hasLoader = listProperties.hasLoader;
    }
    updateValueListProperties(dropdownSearchListComponent, listProperties) {
        dropdownSearchListComponent.displayPropertyName = listProperties.displayPropertyName;
        dropdownSearchListComponent.valuePropertyName = listProperties.valuePropertyName;
        dropdownSearchListComponent.emptyMessage = listProperties.emptyMessage;
        dropdownSearchListComponent.multiple = listProperties.multiple;
        dropdownSearchListComponent.minBufferPx = listProperties.minBufferPx;
        dropdownSearchListComponent.maxBufferPx = listProperties.maxBufferPx;
    }
    updateA11yListProperties(dropdownSearchListComponent, listProperties) {
        dropdownSearchListComponent.loadingText = listProperties.loadingText;
        dropdownSearchListComponent.listId = listProperties.listId;
        dropdownSearchListComponent.ariaLabelInput = listProperties.ariaLabelInput;
        dropdownSearchListComponent.ariaDescriptionInput = listProperties.ariaDescriptionInput;
        dropdownSearchListComponent.ariaLabelClearIcon = listProperties.ariaLabelClearIcon;
        dropdownSearchListComponent.ariaLabelList = listProperties.ariaLabelList;
        dropdownSearchListComponent.ariaLabelledbyList = listProperties.ariaLabelledbyList;
        dropdownSearchListComponent.ariaDescriptionList = listProperties.ariaDescriptionList;
        dropdownSearchListComponent.ariaDescribedbyList = listProperties.ariaDescribedbyList;
    }
    closeDropdownList() {
        this.overlayRef.dispose();
        this._dropdownWidthChangeSubscription.unsubscribe();
    }
    attachDropdownList(overlayRef) {
        const dropdownPortal = new ComponentPortal(WindmillDropdownSearchListComponent);
        this.subscribeToClosingEvents();
        return overlayRef.attach(dropdownPortal);
    }
    createOverlay(elementRef, overlayClass) {
        const positionStrategy = this.getPositionStrategy(elementRef);
        return this.overlay.create(new OverlayConfig({
            hasBackdrop: true,
            backdropClass: 'transparent-backdrop',
            panelClass: ['dropdown-search-panel', overlayClass],
            scrollStrategy: this.overlay.scrollStrategies.reposition(),
            positionStrategy
        }));
    }
    getPositionStrategy(elementRef) {
        const dropdownContainerNativeElement = elementRef.nativeElement;
        const dropdownContainer = dropdownContainerNativeElement.querySelector('.windmill-dropdown-search-container');
        return this.overlay
            .position()
            .flexibleConnectedTo(dropdownContainer)
            .withPositions(this.positions)
            .withFlexibleDimensions(false)
            .withPush(true);
    }
    subscribeToClosingEvents() {
        this._backdropClickEventSubscription = this.overlayRef?.backdropClick().subscribe(() => {
            this.closed.emit(true);
        });
        this._keydownEventSubscription = this.overlayRef?.keydownEvents().subscribe((event) => {
            const isLoaderShowing = this.hasLoader && (typeof this.unfilteredSource === 'undefined' || this.unfilteredSource?.length === 0);
            if (event.key === 'Escape' || (event.key === 'Tab' && isLoaderShowing)) {
                this.closed.emit(true);
            }
            if ((event.key === 'Enter' || event.key === ' ') && isLoaderShowing) {
                event.preventDefault();
            }
        });
    }
    _updateProperties(dropdownSearchListComponent, listProperties) {
        this.updateLayoutListProperties(dropdownSearchListComponent, listProperties);
        this.updateValueListProperties(dropdownSearchListComponent, listProperties);
        this.updateA11yListProperties(dropdownSearchListComponent, listProperties);
    }
    _subscribeToDropdownWidthChange() {
        this._dropdownWidthChangeSubscription = this.dropdownWidthChange.subscribe(width => {
            if (width) {
                this.componentRef.instance.positions.width = width;
                this.componentRef.instance.setListWidthInHtml();
                this.overlayRef?.updatePosition();
            }
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillDropdownSearchOverlayService, deps: [{ token: i1$1.Overlay }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillDropdownSearchOverlayService, providedIn: WindmillDropdownSearchListComponent });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillDropdownSearchOverlayService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: WindmillDropdownSearchListComponent
                }]
        }], ctorParameters: () => [{ type: i1$1.Overlay }] });

class WindmillDropdownValueAccessorDirective extends DefaultValueAccessor {
    injector;
    render2;
    elementRef;
    windmillDropdownSearchOverlayService;
    /**
     * Property is used to set which property of the source will be displayed in the input
     */
    displayPropertyName;
    /**
     * Property is used to set which source property will be set as the input value
     */
    valuePropertyName;
    /**
     * Property is used to enable multiple selection for the component.
     */
    multiple = false;
    /**
     * Set a custom function for validation for errors of component
     */
    validationFunction;
    /**
     * Property can be used to send the list of items to the component.
     */
    set source(newSource) {
        if (!newSource) {
            return;
        }
        this._source = Object.assign(newSource);
        this.dropdownSearchListComponentRef?.instance.filteredSourceProject$.next(newSource);
        if (this.injectedFormControl && !this.isFiltered) {
            this.initialSource = Object.assign(newSource);
            this.writeValue(this.formControlValue);
            this.dropdownSearchListComponentRef?.instance.initialItemsSource$.next(newSource);
        }
        if (!this.isFiltered) {
            this.windmillDropdownSearchOverlayService.setSource(newSource);
        }
    }
    get source() {
        return this._source;
    }
    /**
     * Emits the value which is typed in the search input
     */
    valueChanged = new EventEmitter();
    disabled = false;
    dropdownSearchListComponentRef;
    injectedFormControl;
    displayedValue;
    formControlValue;
    value;
    isFiltered = false;
    initialSource;
    _source;
    constructor(injector, render2, elementRef, windmillDropdownSearchOverlayService) {
        super(render2, elementRef, false);
        this.injector = injector;
        this.render2 = render2;
        this.elementRef = elementRef;
        this.windmillDropdownSearchOverlayService = windmillDropdownSearchOverlayService;
    }
    ngOnInit() {
        this.injectedFormControl = this.injector.get(NgControl);
    }
    writeValue(newValue) {
        this.value = this.getValidValue(newValue);
        if (this.multiple && (Array.isArray(this.value) || !this.value)) {
            this.handleValueMultipleDropdown(this.value);
        }
        else {
            this.handleValueBasicDropdown();
        }
        this.formControlValue = this.value && !this.isFiltered ? this.value : newValue;
        this.dropdownSearchListComponentRef?.instance.preselectedItems$.next(this.formControlValue);
        this.displayInputTextFromStart();
    }
    getValidValue(newValue) {
        const validValue = this.multiple
            ? Array.isArray(newValue)
                ? this.validValueMultiple(newValue)
                : this.validValueMultiple([newValue])
            : newValue;
        return validValue ?? null;
    }
    validValueMultiple(value) {
        const validValue = [];
        value?.forEach(valueItem => {
            const itemSource = this.findItemInSource(valueItem);
            validValue.push(itemSource);
        });
        return validValue.filter(item => typeof item !== 'undefined');
    }
    findItemInSource(valueItem) {
        let elementSearched;
        this.source?.forEach(element => {
            if (element === valueItem) {
                elementSearched = element;
            }
            if (this.hasComplexSource) {
                const foundElement = this.findElementInComplexSource(element, valueItem);
                if (foundElement !== null) {
                    elementSearched = foundElement;
                }
            }
        });
        return elementSearched;
    }
    getFormControl() {
        return this.injector.get(NgControl).control;
    }
    handleValueMultipleDropdown(values) {
        if (!values) {
            this.value = null;
            this.displayedValue = null;
            return;
        }
        this.displayedValue = this.getDisplayedValueForMultiple(values);
    }
    handleValueBasicDropdown() {
        let itemFound = this.source?.find(item => item === this.value);
        if (this.hasComplexSource) {
            itemFound = this.source?.find(item => this.checkComplexItem(item, this.value));
        }
        this.displayedValue = itemFound
            ? this.hasComplexSource
                ? this.getComplexDisplayedValue(itemFound)
                : itemFound
            : null;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    get isInvalidFormControl() {
        if (typeof this.validationFunction !== 'undefined') {
            return this.validationFunction();
        }
        return false;
    }
    get hasComplexSource() {
        const propertiesAreDefined = typeof this.displayPropertyName !== 'undefined';
        const propertiesAreNotEmptyStrings = this.displayPropertyName !== '';
        const propertiesAreNotNull = this.displayPropertyName !== null;
        return propertiesAreDefined && propertiesAreNotNull && propertiesAreNotEmptyStrings;
    }
    removeValue() {
        this.displayedValue = null;
        this.formControlValue = null;
        this.valueChanged.emit(null);
        this.injectedFormControl.control?.patchValue(null);
        this.injectedFormControl.control?.markAsTouched();
        this.injectedFormControl.control?.markAsDirty();
    }
    checkComplexItem(item, value) {
        if (!item) {
            return null;
        }
        return this.doesValueExistsInSource(item, value);
    }
    getComplexValueProperty(item) {
        return this.valuePropertyName
            ? item[this.valuePropertyName]
            : item;
    }
    getComplexDisplayedValue(item) {
        return String(item[this.displayPropertyName]);
    }
    isObjectAndNoValueProperty(elementDisplayValue, valueItem) {
        return (typeof valueItem === 'object' &&
            valueItem !== null &&
            !this.valuePropertyName &&
            elementDisplayValue === this.getComplexDisplayedValue(valueItem));
    }
    isValuePropertyAndNotObject(elementDisplayValue, valueItem) {
        return Boolean(this.valuePropertyName &&
            typeof valueItem !== 'object' &&
            valueItem &&
            elementDisplayValue === this.getComplexDisplayedValue(valueItem));
    }
    findElementInComplexSource(element, valueItem) {
        if (!element) {
            return null;
        }
        const elementDisplayValue = this.getComplexDisplayedValue(element);
        const elementValueProperty = this.getComplexValueProperty(element);
        if (this.isObjectAndNoValueProperty(elementDisplayValue, valueItem) ||
            this.isValuePropertyAndNotObject(elementDisplayValue, valueItem)) {
            return element;
        }
        if (elementValueProperty === valueItem) {
            return elementValueProperty;
        }
        return null;
    }
    doesValueExistsInSource(item, value) {
        const itemValue = this.getComplexValueProperty(item);
        const isValueObject = value && typeof value === 'object';
        if (isValueObject) {
            return this.isValuePropertyEqual(itemValue, value) || this.isDisplayPropertyEqual(itemValue, value);
        }
        return itemValue === value;
    }
    isValuePropertyEqual(itemValue, value) {
        return (typeof this.valuePropertyName !== 'undefined' &&
            this.valuePropertyName !== null &&
            typeof itemValue[this.valuePropertyName] !== 'undefined' &&
            typeof value[this.valuePropertyName] !== 'undefined' &&
            itemValue[this.valuePropertyName] === value[this.valuePropertyName]);
    }
    isDisplayPropertyEqual(itemValue, value) {
        return (typeof this.displayPropertyName !== 'undefined' &&
            this.displayPropertyName !== null &&
            typeof itemValue[this.displayPropertyName] !== 'undefined' &&
            typeof value[this.displayPropertyName] !== 'undefined' &&
            itemValue[this.displayPropertyName] === value[this.displayPropertyName]);
    }
    displayInputTextFromStart() {
        const input = this.elementRef.nativeElement.querySelector('input');
        setTimeout(() => {
            input?.setSelectionRange(0, 0);
        });
    }
    getDisplayedValueForMultiple(values) {
        const displayedValues = [];
        this.value = values.length === 0 ? null : values;
        values.forEach(value => {
            if (this.displayPropertyName) {
                const itemFound = this.source?.find(item => this.checkComplexItem(item, value));
                if (itemFound) {
                    displayedValues.push(this.getComplexDisplayedValue(itemFound));
                }
            }
            else {
                displayedValues.push(value);
            }
        });
        return displayedValues.join(', ');
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillDropdownValueAccessorDirective, deps: [{ token: forwardRef(() => Injector) }, { token: forwardRef(() => Renderer2) }, { token: forwardRef(() => ElementRef) }, { token: WindmillDropdownSearchOverlayService }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.10", type: WindmillDropdownValueAccessorDirective, isStandalone: true, selector: "[windmillDropdownValueAccessorDirective]", inputs: { displayPropertyName: "displayPropertyName", valuePropertyName: "valuePropertyName", multiple: "multiple", validationFunction: "validationFunction", source: "source" }, outputs: { valueChanged: "valueChanged" }, usesInheritance: true, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillDropdownValueAccessorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[windmillDropdownValueAccessorDirective]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.Injector, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => Injector)]
                }] }, { type: i0.Renderer2, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => Renderer2)]
                }] }, { type: i0.ElementRef, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => ElementRef)]
                }] }, { type: WindmillDropdownSearchOverlayService }], propDecorators: { displayPropertyName: [{
                type: Input
            }], valuePropertyName: [{
                type: Input
            }], multiple: [{
                type: Input
            }], validationFunction: [{
                type: Input
            }], source: [{
                type: Input
            }], valueChanged: [{
                type: Output
            }] } });

const WINDMILL_DROPDOWN_SEARCH_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => WindmillDropdownSearchComponent),
    multi: true
};
const ARROW_ICON_SIZE = {
    default: '24',
    extrasmall: '20'
};
const DEFAULT_MAX_LIST_HEIGHT = 480;
/**
 * To use this component in a module, you can import the `WindmillDropdownSearchModule`:
 * @example
 * ```typescript
 * import { WindmillDropdownSearchModule } from '@windmill/ng-windmill/dropdown-search';
 * ```
 *
 * To use the standalone component directly, you can import `WindmillDropdownSearchComponent`:
 * @example
 * ```typescript
 * import { WindmillDropdownSearchComponent } from '@windmill/ng-windmill/dropdown-search';
 * ```
 */
class WindmillDropdownSearchComponent extends WindmillDropdownValueAccessorDirective {
    dropdownSearchOverlayService;
    changeDetector;
    /**
     * The id for dropdown
     */
    id;
    /**
     * Sets the id attribute for the list
     */
    listId;
    /**
     * The data-testid for dropdown
     */
    testInputId;
    /**
     * The data-testid for input from list
     */
    testInputSearchId;
    /**
     * The data-testid for list from overlay
     */
    testListId;
    /**
     * Customizable message in case the list is empty
     */
    emptyMessage = 'No items available';
    /**
     * Placeholder for dropdown
     */
    dropdownPlaceholder;
    /**
     * Placeholder for input from overlay
     */
    listPlaceholder;
    /**
     * Property can be used to set the readonly state
     */
    readonly = false;
    /**
     * Property can be used to set the dropdown to have a clear icon
     */
    hasClearIcon = false;
    /**
     * Property to set different dropdown sizes
     */
    size;
    /**
     * Property could be used for adding some descriptional text to the dropdown.
     */
    title;
    /**
     * Property can be used to set the disabled state.
     */
    disabled = false;
    /**
     * Maximum number of lines for items text
     */
    maxLinesNumber = '1';
    /**
     * Sets the maximum height of the list
     */
    maxHeight = DEFAULT_MAX_LIST_HEIGHT;
    /**
     * Property is used to set custom class that can be added to the component overlay.
     */
    overlayClass;
    /**
     * The minimum amount of buffer rendered beyond the viewport (in pixels). If the amount of buffer dips below this number, more items will be rendered
     */
    minBufferPx = DEFAULT_MIN_BUFFER_PX;
    /**
     * The number of pixels worth of buffer to render for when rendering new items
     */
    maxBufferPx = DEFAULT_MAX_BUFFER_PX;
    /**
     * Property can be used to set a text on the loader for the screen reader
     */
    loadingText;
    /**
     * Sets the aria-label attribute for the dropdown
     */
    ariaLabel;
    /**
     * Sets the aria-description attribute for the dropdown
     */
    ariaDescription;
    /**
     * Sets the aria-labelledby attribute for the dropdown
     */
    ariaLabelledby;
    /**
     * Sets the aria-describedby attribute for the dropdown
     */
    ariaDescribedby;
    /**
     * Sets the aria-label attribute for the clear button
     */
    ariaLabelClearButton;
    /**
     * Sets the aria-label attribute for the input from overlay
     */
    ariaLabelSearchInput;
    /**
     * Sets the aria-description attribute for the input from overlay
     */
    ariaDescriptionSearchInput;
    /**
     * Sets the aria-label attribute for the clear button
     */
    ariaLabelClearButtonSearchInput;
    /**
     * Sets the aria-label attribute for the list
     */
    ariaLabelList;
    /**
     * Sets the aria-description attribute for the list
     */
    ariaDescriptionList;
    /**
     * Sets the aria-labelledby attribute for the list
     */
    ariaLabelledbyList;
    /**
     * Sets the aria-describedby attribute for the list
     */
    ariaDescribedbyList;
    /**
     * Custom CSS background color (e.g. hex, rgba, CSS variable etc.) for the dropdown in an editable state
     */
    backgroundColor;
    /**
     * Property can be used to show a loader in the list. It is set to true by default.
     */
    get hasLoader() {
        return this._hasLoader;
    }
    set hasLoader(value) {
        this._hasLoader = value;
        if (this.isOpened && this.dropdownSearchListComponentRef) {
            this.dropdownSearchListComponentRef.instance.hasLoader$.next(value);
        }
    }
    /**
     * Emits when typing in the filter input or when the value from the filter input is cleared
     */
    searchValueChange = new EventEmitter();
    /**
     * Emits the value when dropdown has lost focus
     */
    innerBlur = new EventEmitter();
    dropdownWrapperElement;
    isOpened = false;
    _hasLoader = true;
    _resizeObserver;
    selectedItemEventSubscription = Subscription.EMPTY;
    searchValueChangeSubscription = Subscription.EMPTY;
    overlayClosedSubscription = Subscription.EMPTY;
    isClosedSubscription = Subscription.EMPTY;
    constructor(dropdownSearchOverlayService, changeDetector, injector) {
        const render2 = injector.get(Renderer2);
        const elementRef = injector.get(ElementRef);
        super(injector, render2, elementRef, dropdownSearchOverlayService);
        this.dropdownSearchOverlayService = dropdownSearchOverlayService;
        this.changeDetector = changeDetector;
    }
    ngOnDestroy() {
        this.unsubscribeAll();
        if (this.isOpened) {
            this.closeOverlay();
        }
    }
    onPopState() {
        if (this.isOpened) {
            this.closeDropdownList();
        }
    }
    onFocusOut(event) {
        if (event.relatedTarget === null || event.relatedTarget?.tagName === 'BUTTON') {
            this.onTouched();
            this.onInnerBlur(event);
        }
    }
    onInnerBlur(event) {
        this.innerBlur.emit(event);
    }
    clearValue() {
        this.removeValue();
        this.getInputElement().focus();
    }
    openOverlay() {
        if (!this.disabled && !this.readonly) {
            this.isOpened = true;
            this.openDropdownList();
            this._initializeResizeObserver();
            this.subscribeToOverlayChanges();
            this.dropdownSearchListComponentRef?.instance.filteredSourceProject$.next(this.source);
            this.dropdownSearchListComponentRef?.instance.initialItemsSource$.next(this.source);
            this.dropdownSearchOverlayService.setSource(this.source);
            this.dropdownSearchListComponentRef?.instance.hasLoader$.next(this.hasLoader);
        }
    }
    openDropdownList() {
        const native = this.elementRef.nativeElement;
        const clientRect = native.getBoundingClientRect();
        const positions = {
            bottom: clientRect.bottom,
            left: clientRect.left,
            width: clientRect.width
        };
        const newListProperties = this.createListProperties(positions);
        this.dropdownSearchListComponentRef = this.dropdownSearchOverlayService.openDropdownList(newListProperties);
        this.dropdownSearchListComponentRef?.instance.preselectedItems$.next(this.value);
        this.subscribeToEvents();
        this.onInnerBlur(null);
    }
    closeDropdownList() {
        this.isOpened = false;
        this.dropdownSearchOverlayService.closeDropdownList();
        if (this.initialSource) {
            this.source = Object.assign(this.initialSource);
        }
        this._resizeObserver.unobserve(this.dropdownWrapperElement.nativeElement);
        this._resizeObserver.disconnect();
    }
    getInputElement() {
        return this.elementRef.nativeElement.getElementsByTagName('button')[0];
    }
    get ariaExpandedValue() {
        return this.readonly ? null : this.isOpened;
    }
    get isEditable() {
        return !this.readonly && !this.disabled;
    }
    get role() {
        return this.readonly ? null : 'combobox';
    }
    get shouldShowValue() {
        return (typeof this.displayedValue !== 'undefined' &&
            this.displayedValue !== null &&
            this.displayedValue !== '' &&
            this.displayedValue.toString() !== '');
    }
    get shouldShowClearButton() {
        const hasValidDisplayedValue = this.displayedValue !== null && typeof this.displayedValue !== 'undefined' && this.displayedValue !== '';
        return this.hasClearIcon && hasValidDisplayedValue && !this.disabled && !this.readonly;
    }
    get hasValidValue() {
        const hasValidDisplayedValue = this.displayedValue !== null && typeof this.displayedValue !== 'undefined' && this.displayedValue !== '';
        const hasValidFormControlValue = this.formControlValue !== null && typeof this.formControlValue !== 'undefined' && this.formControlValue !== '';
        return hasValidDisplayedValue || hasValidFormControlValue;
    }
    get nonEditablePlaceholder() {
        return this.readonly && !this.hasValidValue ? '-' : this.dropdownPlaceholder;
    }
    get arrowIconSize() {
        return this.size === 'extrasmall' ? ARROW_ICON_SIZE.extrasmall : ARROW_ICON_SIZE.default;
    }
    get placeholderValue() {
        return this.readonly ? '-' : this.dropdownPlaceholder;
    }
    get isRequiredFormControl() {
        return this.getFormControl().hasValidator(Validators.required) && !this.disabled && !this.readonly;
    }
    get disabledAttributeValue() {
        return this.disabled ? true : null;
    }
    get backgroundColorValue() {
        return this.readonly || this.disabled ? null : this.backgroundColor;
    }
    updateValue(newValue) {
        this.writeValue(newValue);
        this.onChange(newValue);
        this.valueChanged.emit(newValue);
        this.injectedFormControl.control?.markAsTouched();
    }
    updateDisplayedValueForMultiple(newValues) {
        const displayedValues = [];
        newValues.forEach(value => {
            const item = this.initialSource?.find(item => this.checkComplexItem(item, value));
            if (item) {
                displayedValues.push(this.getComplexDisplayedValue(item));
            }
        });
        this.displayedValue = displayedValues.join(', ');
    }
    closeOverlay() {
        this.closeDropdownList();
        this.getInputElement().focus();
    }
    createListProperties(positions) {
        return {
            displayPropertyName: this.displayPropertyName,
            valuePropertyName: this.valuePropertyName,
            isOpened: this.isOpened,
            positions,
            emptyMessage: this.emptyMessage,
            listPlaceholder: this.listPlaceholder,
            elementRef: this.elementRef,
            testInputSearchId: this.testInputSearchId,
            listId: this.listId,
            testListId: this.testListId,
            multiple: this.multiple,
            maxHeight: this.maxHeight,
            maxLinesNumber: this.maxLinesNumber,
            hasLoader: this.hasLoader,
            overlayClass: this.overlayClass,
            minBufferPx: this.minBufferPx,
            maxBufferPx: this.maxBufferPx,
            loadingText: this.loadingText,
            ariaLabelInput: this.ariaLabelSearchInput,
            ariaDescriptionInput: this.ariaDescriptionSearchInput,
            ariaLabelClearIcon: this.ariaLabelClearButtonSearchInput,
            ariaLabelList: this.ariaLabelList,
            ariaDescriptionList: this.ariaDescriptionList,
            ariaLabelledbyList: this.ariaLabelledbyList,
            ariaDescribedbyList: this.ariaDescribedbyList
        };
    }
    subscribeToEvents() {
        if (this.multiple) {
            this.subscribeToMultipleSelect();
        }
        else {
            this.subscribeToSelectedItem();
        }
        this.subscribeToIsClosedEvent();
        this.subscribeToSearchValueChange();
    }
    subscribeToIsClosedEvent() {
        this.isClosedSubscription = this.dropdownSearchListComponentRef.instance.isClosed.subscribe(() => {
            this.closeOverlay();
        });
    }
    subscribeToOverlayChanges() {
        this.overlayClosedSubscription = this.dropdownSearchOverlayService.closed.subscribe(() => {
            if (this.isOpened) {
                this.injectedFormControl.control?.markAsTouched();
            }
            this.closeDropdownList();
            this.changeDetector.detectChanges();
        });
    }
    subscribeToMultipleSelect() {
        this.selectedItemEventSubscription = this.dropdownSearchListComponentRef.instance.multipleDropdownSearch$.subscribe(itemsSource => {
            if (!itemsSource) {
                return;
            }
            const values = this.getSelectedValues(itemsSource);
            this.updateValue(values);
            if (this.displayPropertyName) {
                this.updateDisplayedValueForMultiple(values);
            }
        });
    }
    getSelectedValues(itemsSource) {
        const selectedValues = [];
        itemsSource.forEach(item => {
            if (item.checked) {
                const newValue = item.itemWithChecked && this.displayPropertyName
                    ? this.getComplexValueProperty(item.itemWithChecked)
                    : item.itemWithChecked;
                selectedValues.push(newValue);
            }
        });
        return selectedValues;
    }
    subscribeToSelectedItem() {
        this.selectedItemEventSubscription = this.dropdownSearchListComponentRef.instance.selectedItem$.subscribe(selectedValue => {
            if (!selectedValue && selectedValue !== 0) {
                return;
            }
            if (this.displayPropertyName) {
                const item = this.initialSource?.find(item => this.checkComplexItem(item, selectedValue));
                this.displayedValue = typeof item === 'object' && item !== null ? item[this.displayPropertyName] : String(item);
            }
            this.updateValue(selectedValue);
            this.closeOverlay();
        });
    }
    subscribeToSearchValueChange() {
        this.searchValueChangeSubscription = this.dropdownSearchListComponentRef.instance.searchValueChange$.subscribe(data => {
            this.searchValueChange.emit(typeof data === 'undefined' || data === null ? '' : String(data));
            this.isFiltered = Boolean(data);
        });
    }
    unsubscribeAll() {
        this.selectedItemEventSubscription.unsubscribe();
        this.searchValueChangeSubscription.unsubscribe();
        this.overlayClosedSubscription.unsubscribe();
        this.isClosedSubscription.unsubscribe();
    }
    _initializeResizeObserver() {
        this._resizeObserver = new ResizeObserver((entries) => {
            entries.forEach(entry => {
                const width = entry.contentRect.width;
                this.dropdownSearchOverlayService.dropdownWidthChange.next(width);
            });
        });
        this._resizeObserver.observe(this.dropdownWrapperElement.nativeElement);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillDropdownSearchComponent, deps: [{ token: WindmillDropdownSearchOverlayService }, { token: i0.ChangeDetectorRef }, { token: forwardRef(() => Injector) }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: WindmillDropdownSearchComponent, isStandalone: true, selector: "windmill-dropdown-search", inputs: { id: "id", listId: "listId", testInputId: "testInputId", testInputSearchId: "testInputSearchId", testListId: "testListId", emptyMessage: "emptyMessage", dropdownPlaceholder: "dropdownPlaceholder", listPlaceholder: "listPlaceholder", readonly: "readonly", hasClearIcon: "hasClearIcon", size: "size", title: "title", disabled: "disabled", maxLinesNumber: "maxLinesNumber", maxHeight: "maxHeight", overlayClass: "overlayClass", minBufferPx: "minBufferPx", maxBufferPx: "maxBufferPx", loadingText: "loadingText", ariaLabel: "ariaLabel", ariaDescription: "ariaDescription", ariaLabelledby: "ariaLabelledby", ariaDescribedby: "ariaDescribedby", ariaLabelClearButton: "ariaLabelClearButton", ariaLabelSearchInput: "ariaLabelSearchInput", ariaDescriptionSearchInput: "ariaDescriptionSearchInput", ariaLabelClearButtonSearchInput: "ariaLabelClearButtonSearchInput", ariaLabelList: "ariaLabelList", ariaDescriptionList: "ariaDescriptionList", ariaLabelledbyList: "ariaLabelledbyList", ariaDescribedbyList: "ariaDescribedbyList", backgroundColor: "backgroundColor", hasLoader: "hasLoader" }, outputs: { searchValueChange: "searchValueChange", innerBlur: "innerBlur" }, host: { listeners: { "window:popstate": "onPopState()" }, properties: { "class": "this.size" } }, providers: [WINDMILL_DROPDOWN_SEARCH_VALUE_ACCESSOR, WindmillDropdownSearchOverlayService], viewQueries: [{ propertyName: "dropdownWrapperElement", first: true, predicate: ["dropdownWrapper"], descendants: true }], usesInheritance: true, ngImport: i0, template: "<div\r\n  #dropdownWrapper\r\n  class=\"windmill-dropdown-search-container\"\r\n  [class.has-value]=\"shouldShowValue\"\r\n  [class.has-clear-icon]=\"hasClearIcon\"\r\n  [class.is-disabled]=\"disabled\"\r\n  [class.is-invalid]=\"isInvalidFormControl\"\r\n  [class.is-readonly]=\"readonly\"\r\n  [ngClass]=\"size\">\r\n  @if (isEditable) {\r\n    <button\r\n      class=\"dropdown-search-input\"\r\n      role=\"combobox\"\r\n      type=\"button\"\r\n      aria-haspopup=\"listbox\"\r\n      [attr.id]=\"id\"\r\n      [attr.title]=\"title\"\r\n      [attr.aria-controls]=\"listId\"\r\n      [attr.data-testid]=\"testInputId\"\r\n      [attr.aria-expanded]=\"isOpened\"\r\n      [attr.aria-label]=\"ariaLabel\"\r\n      [attr.aria-labelledby]=\"ariaLabelledby\"\r\n      [attr.aria-description]=\"ariaDescription\"\r\n      [attr.aria-describedby]=\"ariaDescribedby\"\r\n      [attr.aria-invalid]=\"isInvalidFormControl\"\r\n      [attr.aria-required]=\"isRequiredFormControl\"\r\n      [style.backgroundColor]=\"backgroundColorValue\"\r\n      (click)=\"openOverlay()\"\r\n      (focusout)=\"onFocusOut($event)\">\r\n      <span class=\"dropdown-search-input-content\">\r\n        @if (shouldShowValue) {\r\n          <span class=\"value\">\r\n            {{ displayedValue }}\r\n          </span>\r\n        } @else {\r\n          <span class=\"placeholder\">{{ dropdownPlaceholder }}</span>\r\n        }\r\n      </span>\r\n      <centric-icon2\r\n        class=\"arrow-icon\"\r\n        [svgIcon]=\"isOpened ? 'chevron-large-up_r' : 'chevron-large-down_r'\"\r\n        [size]=\"arrowIconSize\">\r\n      </centric-icon2>\r\n    </button>\r\n    @if (shouldShowClearButton) {\r\n      <button\r\n        class=\"clear-button\"\r\n        type=\"button\"\r\n        [attr.aria-label]=\"ariaLabelClearButton\"\r\n        (click)=\"clearValue()\">\r\n        <centric-icon2\r\n          [svgIcon]=\"'cancel_r'\"\r\n          [size]=\"'16'\"></centric-icon2>\r\n      </button>\r\n    }\r\n  } @else {\r\n    <div\r\n      class=\"windmill-input\"\r\n      [class.is-disabled]=\"disabled\"\r\n      [class.is-readonly]=\"readonly\"\r\n      [class.has-value]=\"value\"\r\n      [ngClass]=\"size\">\r\n      <input\r\n        #inputReadonlyElement\r\n        type=\"text\"\r\n        class=\"input-element\"\r\n        [attr.title]=\"title\"\r\n        [attr.role]=\"role\"\r\n        [attr.readonly]=\"readonly\"\r\n        [attr.disabled]=\"disabledAttributeValue\"\r\n        [attr.value]=\"displayedValue ? displayedValue : formControlValue\"\r\n        [attr.id]=\"id\"\r\n        [attr.aria-label]=\"ariaLabel\"\r\n        [attr.aria-labelledby]=\"ariaLabelledby\"\r\n        [attr.aria-description]=\"ariaDescription\"\r\n        [attr.aria-describedby]=\"ariaDescribedby\"\r\n        [attr.data-testid]=\"testInputId\"\r\n        [attr.placeholder]=\"nonEditablePlaceholder\"\r\n        [attr.aria-expanded]=\"ariaExpandedValue\" />\r\n      @if (disabled) {\r\n        <centric-icon2\r\n          class=\"arrow-icon\"\r\n          [svgIcon]=\"'chevron-large-down_r'\"\r\n          [size]=\"arrowIconSize\">\r\n        </centric-icon2>\r\n      }\r\n    </div>\r\n  }\r\n</div>\r\n@if (isInvalidFormControl) {\r\n  <div class=\"centric-error-messages\">\r\n    <ng-content select=\"[errors]\"></ng-content>\r\n  </div>\r\n}\r\n", dependencies: [{ kind: "ngmodule", type: A11yModule }, { kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i2$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "ngmodule", type: FormsModule }, { kind: "ngmodule", type: ReactiveFormsModule }, { kind: "ngmodule", type: ScrollingModule }, { kind: "component", type: CentricIcon2Component, selector: "centric-icon2", inputs: ["size", "inline", "svgIcon"] }], encapsulation: i0.ViewEncapsulation.None });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillDropdownSearchComponent, decorators: [{
            type: Component,
            args: [{ selector: 'windmill-dropdown-search', encapsulation: ViewEncapsulation.None, imports: [A11yModule, CommonModule, FormsModule, NgClass, ReactiveFormsModule, ScrollingModule, CentricIcon2Component], providers: [WINDMILL_DROPDOWN_SEARCH_VALUE_ACCESSOR, WindmillDropdownSearchOverlayService], standalone: true, template: "<div\r\n  #dropdownWrapper\r\n  class=\"windmill-dropdown-search-container\"\r\n  [class.has-value]=\"shouldShowValue\"\r\n  [class.has-clear-icon]=\"hasClearIcon\"\r\n  [class.is-disabled]=\"disabled\"\r\n  [class.is-invalid]=\"isInvalidFormControl\"\r\n  [class.is-readonly]=\"readonly\"\r\n  [ngClass]=\"size\">\r\n  @if (isEditable) {\r\n    <button\r\n      class=\"dropdown-search-input\"\r\n      role=\"combobox\"\r\n      type=\"button\"\r\n      aria-haspopup=\"listbox\"\r\n      [attr.id]=\"id\"\r\n      [attr.title]=\"title\"\r\n      [attr.aria-controls]=\"listId\"\r\n      [attr.data-testid]=\"testInputId\"\r\n      [attr.aria-expanded]=\"isOpened\"\r\n      [attr.aria-label]=\"ariaLabel\"\r\n      [attr.aria-labelledby]=\"ariaLabelledby\"\r\n      [attr.aria-description]=\"ariaDescription\"\r\n      [attr.aria-describedby]=\"ariaDescribedby\"\r\n      [attr.aria-invalid]=\"isInvalidFormControl\"\r\n      [attr.aria-required]=\"isRequiredFormControl\"\r\n      [style.backgroundColor]=\"backgroundColorValue\"\r\n      (click)=\"openOverlay()\"\r\n      (focusout)=\"onFocusOut($event)\">\r\n      <span class=\"dropdown-search-input-content\">\r\n        @if (shouldShowValue) {\r\n          <span class=\"value\">\r\n            {{ displayedValue }}\r\n          </span>\r\n        } @else {\r\n          <span class=\"placeholder\">{{ dropdownPlaceholder }}</span>\r\n        }\r\n      </span>\r\n      <centric-icon2\r\n        class=\"arrow-icon\"\r\n        [svgIcon]=\"isOpened ? 'chevron-large-up_r' : 'chevron-large-down_r'\"\r\n        [size]=\"arrowIconSize\">\r\n      </centric-icon2>\r\n    </button>\r\n    @if (shouldShowClearButton) {\r\n      <button\r\n        class=\"clear-button\"\r\n        type=\"button\"\r\n        [attr.aria-label]=\"ariaLabelClearButton\"\r\n        (click)=\"clearValue()\">\r\n        <centric-icon2\r\n          [svgIcon]=\"'cancel_r'\"\r\n          [size]=\"'16'\"></centric-icon2>\r\n      </button>\r\n    }\r\n  } @else {\r\n    <div\r\n      class=\"windmill-input\"\r\n      [class.is-disabled]=\"disabled\"\r\n      [class.is-readonly]=\"readonly\"\r\n      [class.has-value]=\"value\"\r\n      [ngClass]=\"size\">\r\n      <input\r\n        #inputReadonlyElement\r\n        type=\"text\"\r\n        class=\"input-element\"\r\n        [attr.title]=\"title\"\r\n        [attr.role]=\"role\"\r\n        [attr.readonly]=\"readonly\"\r\n        [attr.disabled]=\"disabledAttributeValue\"\r\n        [attr.value]=\"displayedValue ? displayedValue : formControlValue\"\r\n        [attr.id]=\"id\"\r\n        [attr.aria-label]=\"ariaLabel\"\r\n        [attr.aria-labelledby]=\"ariaLabelledby\"\r\n        [attr.aria-description]=\"ariaDescription\"\r\n        [attr.aria-describedby]=\"ariaDescribedby\"\r\n        [attr.data-testid]=\"testInputId\"\r\n        [attr.placeholder]=\"nonEditablePlaceholder\"\r\n        [attr.aria-expanded]=\"ariaExpandedValue\" />\r\n      @if (disabled) {\r\n        <centric-icon2\r\n          class=\"arrow-icon\"\r\n          [svgIcon]=\"'chevron-large-down_r'\"\r\n          [size]=\"arrowIconSize\">\r\n        </centric-icon2>\r\n      }\r\n    </div>\r\n  }\r\n</div>\r\n@if (isInvalidFormControl) {\r\n  <div class=\"centric-error-messages\">\r\n    <ng-content select=\"[errors]\"></ng-content>\r\n  </div>\r\n}\r\n" }]
        }], ctorParameters: () => [{ type: WindmillDropdownSearchOverlayService }, { type: i0.ChangeDetectorRef }, { type: i0.Injector, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => Injector)]
                }] }], propDecorators: { id: [{
                type: Input
            }], listId: [{
                type: Input
            }], testInputId: [{
                type: Input
            }], testInputSearchId: [{
                type: Input
            }], testListId: [{
                type: Input
            }], emptyMessage: [{
                type: Input
            }], dropdownPlaceholder: [{
                type: Input
            }], listPlaceholder: [{
                type: Input
            }], readonly: [{
                type: Input
            }], hasClearIcon: [{
                type: Input
            }], size: [{
                type: HostBinding,
                args: ['class']
            }, {
                type: Input
            }], title: [{
                type: Input
            }], disabled: [{
                type: Input
            }], maxLinesNumber: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], overlayClass: [{
                type: Input
            }], minBufferPx: [{
                type: Input
            }], maxBufferPx: [{
                type: Input
            }], loadingText: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabelledby: [{
                type: Input
            }], ariaDescribedby: [{
                type: Input
            }], ariaLabelClearButton: [{
                type: Input
            }], ariaLabelSearchInput: [{
                type: Input
            }], ariaDescriptionSearchInput: [{
                type: Input
            }], ariaLabelClearButtonSearchInput: [{
                type: Input
            }], ariaLabelList: [{
                type: Input
            }], ariaDescriptionList: [{
                type: Input
            }], ariaLabelledbyList: [{
                type: Input
            }], ariaDescribedbyList: [{
                type: Input
            }], backgroundColor: [{
                type: Input
            }], hasLoader: [{
                type: Input
            }], searchValueChange: [{
                type: Output
            }], innerBlur: [{
                type: Output
            }], dropdownWrapperElement: [{
                type: ViewChild,
                args: ['dropdownWrapper']
            }], onPopState: [{
                type: HostListener,
                args: ['window:popstate']
            }] } });

const windmillModules = [CentricIcon2Module, WindmillLoaderModule];
const declaredModuleComponents = [
    WindmillDropdownSearchComponent,
    WindmillDropdownSearchListComponent,
    WindmillDropdownValueAccessorDirective,
    WindmillItemListComponent
];
const exportedModuleComponents = [
    WindmillDropdownSearchComponent,
    WindmillDropdownSearchListComponent,
    WindmillDropdownValueAccessorDirective
];
class WindmillDropdownSearchModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillDropdownSearchModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: WindmillDropdownSearchModule, imports: [A11yModule,
            CommonModule,
            FormsModule,
            ReactiveFormsModule,
            ScrollingModule, CentricIcon2Module, WindmillLoaderModule, WindmillDropdownSearchComponent,
            WindmillDropdownSearchListComponent,
            WindmillDropdownValueAccessorDirective,
            WindmillItemListComponent], exports: [WindmillDropdownSearchComponent,
            WindmillDropdownSearchListComponent,
            WindmillDropdownValueAccessorDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillDropdownSearchModule, providers: [
            WindmillDropdownSearchOverlayService,
            Overlay,
            { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { float: 'never' } }
        ], imports: [A11yModule,
            CommonModule,
            FormsModule,
            ReactiveFormsModule,
            ScrollingModule, windmillModules, WindmillDropdownSearchComponent,
            WindmillDropdownSearchListComponent] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillDropdownSearchModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        A11yModule,
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                        ScrollingModule,
                        ...windmillModules,
                        ...declaredModuleComponents
                    ],
                    exports: [...exportedModuleComponents],
                    providers: [
                        WindmillDropdownSearchOverlayService,
                        Overlay,
                        { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { float: 'never' } }
                    ],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { WINDMILL_DROPDOWN_LIST_VALUE_ACCESSOR, WINDMILL_DROPDOWN_SEARCH_VALUE_ACCESSOR, WindmillDropdownSearchComponent, WindmillDropdownSearchListComponent, WindmillDropdownSearchModule, WindmillDropdownSearchOverlayService, WindmillDropdownValueAccessorDirective };
